import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

interface ConfirmationDialogProps {
    open: boolean;
    title: string | React.ReactNode;
    message: string | React.ReactNode;
    onConfirm: () => void;
    onCancel: () => void;
    confirmText?: string | React.ReactNode;
    cancelText?: string | React.ReactNode;
    mode?:
        | 'error'
        | 'primary'
        | 'inherit'
        | 'secondary'
        | 'success'
        | 'info'
        | 'warning';
    /** Optional keyboard event handler to use when dialog is open*/
    onKeydown?: (event: KeyboardEvent) => void;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
    open,
    title,
    message,
    onConfirm,
    onCancel,
    confirmText,
    cancelText,
    mode = 'error',
    onKeydown,
}) => {
    // Add or remove keydown event listener based on dialog open state
    useEffect(() => {
        if (open && onKeydown) {
            document.addEventListener('keydown', onKeydown);
        }

        if (!open && onKeydown) {
            document.removeEventListener('keydown', onKeydown);
        }

        return () => {
            if (onKeydown) {
                document.removeEventListener('keydown', onKeydown);
            }
        };
    }, [open, onKeydown]);

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            aria-labelledby="confirmation-dialog-title"
            aria-describedby="confirmation-dialog-description"
            PaperProps={{ elevation: 0 }}
        >
            <DialogTitle id="confirmation-dialog-title">{title}</DialogTitle>
            <DialogContent>
                <DialogContentText id="confirmation-dialog-description">
                    {message}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onCancel}
                    color="primary"
                    autoFocus
                    data-testid="confirmation-dialog-cancel"
                >
                    {cancelText || <FormattedMessage id="cancel" />}
                </Button>
                <Button
                    onClick={onConfirm}
                    color={mode}
                    data-testid="confirmation-dialog-confirm"
                >
                    {confirmText || <FormattedMessage id="confirm" />}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmationDialog;
